import { signal, computed } from '@angular/core';
import { DeliveryNoteData as BaseDeliveryNoteData } from '@app/shared/models/store-types';

// Re-export the type for backward compatibility
export type DeliveryNoteWithOrgSiteKey = BaseDeliveryNoteData;
export type DeliveryNoteData = BaseDeliveryNoteData;

export interface DeliveryNotesState {
    deliveryNotes: DeliveryNoteWithOrgSiteKey[];
    loading: boolean;
    error: string | null;
    lastUpdated: number;
}

const initialState: DeliveryNotesState = {
    deliveryNotes: [],
    loading: false,
    error: null,
    lastUpdated: 0,
};

export const deliveryNotesState = signal<DeliveryNotesState>(initialState);

// Computed values
export const getDeliveryNotes = computed(
    () => deliveryNotesState().deliveryNotes
);
export const isLoading = computed(() => deliveryNotesState().loading);
export const getError = computed(() => deliveryNotesState().error);
export const getLastUpdated = computed(() => deliveryNotesState().lastUpdated);

// Computed derived values
export const getDeliveryNotesCount = computed(
    () => deliveryNotesState().deliveryNotes.length
);

// Role-specific getters removed - use getDeliveryNotes() directly for all roles

// Computed filters and search
export const getFilteredDeliveryNotes = computed(() => {
    const notes = deliveryNotesState().deliveryNotes;
    // Could add filtering logic here if needed
    return notes;
});

// Data change tracking
export const dataChangeSignal = signal<{
    type: 'add' | 'update' | 'delete' | 'refresh' | 'load';
    data?: DeliveryNoteWithOrgSiteKey;
    timestamp: number;
}>({
    type: 'refresh',
    data: undefined,
    timestamp: Date.now(),
});

// Get data change signal
export const getDataChangeSignal = computed(() => dataChangeSignal());

// Trigger signals for data operations
export const triggerDataChange = (
    type: 'add' | 'update' | 'delete' | 'refresh' | 'load',
    data?: DeliveryNoteWithOrgSiteKey
) => {
    dataChangeSignal.set({
        type,
        data,
        timestamp: Date.now(),
    });
};

// Actions
export const setLoading = (loading: boolean) => {
    deliveryNotesState.update(state => ({
        ...state,
        loading,
    }));
};

export const setError = (error: string | null) => {
    deliveryNotesState.update(state => ({
        ...state,
        error,
    }));
};

export const setDeliveryNotes = (
    deliveryNotes: DeliveryNoteWithOrgSiteKey[]
) => {
    deliveryNotesState.update(state => ({
        ...state,
        deliveryNotes,
        lastUpdated: Date.now(),
    }));
    triggerDataChange('load');
};

export const addDeliveryNote = (deliveryNote: DeliveryNoteWithOrgSiteKey) => {
    deliveryNotesState.update(state => ({
        ...state,
        deliveryNotes: [...state.deliveryNotes, deliveryNote],
        lastUpdated: Date.now(),
    }));
    triggerDataChange('add', deliveryNote);
};

export const updateDeliveryNote = (updatedNote: DeliveryNoteWithOrgSiteKey) => {
    deliveryNotesState.update(state => ({
        ...state,
        deliveryNotes: state.deliveryNotes.map(note =>
            note._key === updatedNote._key ? updatedNote : note
        ),
        lastUpdated: Date.now(),
    }));
    triggerDataChange('update', updatedNote);
};

export const removeDeliveryNote = (noteKey: string) => {
    const noteToRemove = deliveryNotesState().deliveryNotes.find(
        note => note._key === noteKey
    );
    deliveryNotesState.update(state => ({
        ...state,
        deliveryNotes: state.deliveryNotes.filter(
            note => note._key !== noteKey
        ),
        lastUpdated: Date.now(),
    }));
    if (noteToRemove) {
        triggerDataChange('delete', noteToRemove);
    }
};

export const clearDeliveryNotes = () => {
    deliveryNotesState.update(state => ({
        ...state,
        deliveryNotes: [],
        lastUpdated: Date.now(),
    }));
    triggerDataChange('refresh');
};

// Enhanced refresh function
export const refreshDeliveryNotes = () => {
    triggerDataChange('refresh');
};

// Optimistic update functions
export const optimisticUpdateDeliveryNote = (
    updatedNote: DeliveryNoteWithOrgSiteKey
) => {
    updateDeliveryNote(updatedNote);
};

export const optimisticAddDeliveryNote = (
    deliveryNote: DeliveryNoteWithOrgSiteKey
) => {
    addDeliveryNote(deliveryNote);
};

export const optimisticRemoveDeliveryNote = (noteKey: string) => {
    removeDeliveryNote(noteKey);
};

// Bulk operations
export const bulkUpdateDeliveryNotes = (
    updatedNotes: DeliveryNoteWithOrgSiteKey[]
) => {
    deliveryNotesState.update(state => {
        const noteMap = new Map(
            state.deliveryNotes.map(note => [note._key, note])
        );
        updatedNotes.forEach(updatedNote => {
            noteMap.set(updatedNote._key, updatedNote);
        });
        return {
            ...state,
            deliveryNotes: Array.from(noteMap.values()),
            lastUpdated: Date.now(),
        };
    });
    triggerDataChange('refresh');
};

export const bulkAddDeliveryNotes = (
    newNotes: DeliveryNoteWithOrgSiteKey[]
) => {
    deliveryNotesState.update(state => ({
        ...state,
        deliveryNotes: [...state.deliveryNotes, ...newNotes],
        lastUpdated: Date.now(),
    }));
    triggerDataChange('refresh');
};

// State synchronization with external changes

// Legacy computed aliases for API compatibility (kept for existing components)
export const isDeliveryNotesLoading = isLoading;
export const getDeliveryNotesError = getError;
export const getDeliveryNotesLastUpdated = getLastUpdated;

// Legacy pagination functions for existing components that haven't been migrated yet
export const getPaginationInfo = computed(() => ({
    hasMore: false,
    limit: 20,
    offset: 0,
    totalCount: 0,
}));
// Pagination values now come from centralized searchStore
export const getTotalCount = computed(() => {
    const { searchState } = require('@app/stores/searchStore');
    return searchState().pagination.totalCount;
});
export const getHasMore = computed(() => {
    const { searchState } = require('@app/stores/searchStore');
    return searchState().pagination.hasMore;
});
export const getLimit = computed(() => {
    const { searchState } = require('@app/stores/searchStore');
    return searchState().pagination.limit;
});
export const getOffset = computed(() => {
    const { searchState } = require('@app/stores/searchStore');
    return searchState().pagination.offset;
});
